import { z } from 'zod';

import type { ActivityDefinition } from './types.js';

import { correctAnswerIssues, feedbackIssues, trimmed } from './_checks.js';
import { draftFields, Option, optionText, optionValue, questionFields } from './_shared.js';
import { activityIssue, asList, asRecord } from './types.js';

const issue = activityIssue('drag_pairing');

export const DragPair = z.looseObject({
  item: Option.optional(),
  option: Option.optional(),
});

/**
 * Drag each item onto its match. A pair with only one side written renders half a row
 * the player can never complete, which is the failure mode worth catching — a producer
 * handed four terms and three definitions will happily write the fourth pair empty.
 */
export const dragPairing: ActivityDefinition = {
  type: 'drag_pairing',
  summary: 'Drag each item onto the thing it matches.',
  setup: z.looseObject({
    ...draftFields,
    ...questionFields,
    pairs: z.array(DragPair).optional(),
    correctAnswerOrder: z.array(z.string()).optional(),
  }),
  check: (setup, ctx) => {
    const pairs = asList(setup.pairs);
    if (!pairs.length) {
      return [issue('empty', 'No pairs are written — there is nothing to match.', [...ctx.path, 'setup', 'pairs'], ctx)];
    }

    const matches = pairs.map(pair => asRecord(pair).option);
    const issues = [
      ...feedbackIssues('drag_pairing', setup.feedback, ctx),
    ];
    for (const [index, raw] of pairs.entries()) {
      const pair = asRecord(raw);
      const item = optionText(asRecord(pair.item));
      const match = optionText(asRecord(pair.option));
      if (!item || !match) {
        issues.push(issue(
          'halfPair',
          `Pair ${index + 1} is missing its ${item ? 'match' : 'item'} — a pair needs both sides written.`,
          [...ctx.path, 'setup', 'pairs', index],
          ctx,
          raw,
        ));
      }
      for (const [side, label] of [['item', 'item'], ['option', 'match']] as const) {
        if (optionText(asRecord(pair[side])) && !optionValue(asRecord(pair[side]) as Option)) {
          issues.push(issue('optionValue', `Pair ${index + 1}'s ${label} has no value, so no answer can name it.`, [...ctx.path, 'setup', 'pairs', index, side, 'value'], ctx, raw));
        }
      }
    }

    // `correctAnswerOrder` lists each pair's match value, in pair order: one per pair.
    const order = asList(setup.correctAnswerOrder).map(trimmed).filter(Boolean);
    if (order.length) {
      const values = matches.map(match => optionValue(asRecord(match) as Option)).filter(Boolean);
      issues.push(...correctAnswerIssues({ type: 'drag_pairing', field: 'correctAnswerOrder', answers: order, values, ctx, indexed: true }));
      if (order.length !== pairs.length) {
        issues.push(issue(
          'incompleteOrder',
          `correctAnswerOrder has ${order.length} entries for ${pairs.length} pairs; it needs one match per pair.`,
          [...ctx.path, 'setup', 'correctAnswerOrder'],
          ctx,
          order,
        ));
      }
    }

    return issues;
  },
};
